/**
 * Guest user identifier constant
 */
const GUEST_USER_ID = 'guest';

/**
 * Key separator used to build composite keys
 */
const KEY_SEPARATOR = ':';

/**
 * Parsed components of a scoped storage key
 */
export interface ScopedKeyComponents {
  userId: string;
  deviceId: string;
  baseKey: string;
}

/**
 * ScopedStorageKeyBuilder
 * Utility class for building and parsing composite storage keys.
 * Key format: [userId]:[deviceId]:[baseKey]
 */
export class ScopedStorageKeyBuilder {
  /**
   * Build a scoped storage key
   * @param userId - User ID (null treated as guest)
   * @param deviceId - Device ID
   * @param baseKey - Original storage key
   * @returns Composite scoped key
   */
  static buildKey(userId: string | null, deviceId: string, baseKey: string): string {
    const effectiveUserId = userId ?? GUEST_USER_ID;
    return `${effectiveUserId}${KEY_SEPARATOR}${deviceId}${KEY_SEPARATOR}${baseKey}`;
  }

  /**
   * Parse a scoped key into its components
   * @param scopedKey - The composite scoped key
   * @returns Parsed components or null if invalid
   */
  static parseKey(scopedKey: string): ScopedKeyComponents | null {
    const parts = scopedKey.split(KEY_SEPARATOR);
    
    // Must have at least 3 parts: userId, deviceId, and baseKey
    // baseKey might contain separators, so we rejoin the rest
    if (parts.length < 3) {
      return null;
    }

    const userId = parts[0];
    const deviceId = parts[1];
    const baseKey = parts.slice(2).join(KEY_SEPARATOR);

    if (!userId || !deviceId || !baseKey) {
      return null;
    }

    return { userId, deviceId, baseKey };
  }

  /**
   * Check if a key matches a specific scope
   * @param key - The key to check
   * @param userId - User ID to match (null matches guest)
   * @param deviceId - Device ID to match
   * @returns True if key belongs to the specified scope
   */
  static matchesScope(key: string, userId: string | null, deviceId: string): boolean {
    const components = this.parseKey(key);
    if (!components) {
      return false;
    }

    const effectiveUserId = userId ?? GUEST_USER_ID;
    return components.userId === effectiveUserId && components.deviceId === deviceId;
  }

  /**
   * Extract the base key from a scoped key
   * @param scopedKey - The composite scoped key
   * @returns The original base key or null if invalid
   */
  static extractBaseKey(scopedKey: string): string | null {
    const components = this.parseKey(scopedKey);
    return components?.baseKey ?? null;
  }

  /**
   * Check if a key is a scoped key (has the correct format)
   * @param key - The key to check
   * @returns True if the key appears to be a scoped key
   */
  static isScopedKey(key: string): boolean {
    return this.parseKey(key) !== null;
  }

  /**
   * Get the prefix for a specific scope (useful for filtering)
   * @param userId - User ID (null treated as guest)
   * @param deviceId - Device ID
   * @returns The prefix string for the scope
   */
  static getScopePrefix(userId: string | null, deviceId: string): string {
    const effectiveUserId = userId ?? GUEST_USER_ID;
    return `${effectiveUserId}${KEY_SEPARATOR}${deviceId}${KEY_SEPARATOR}`;
  }

  /**
   * Get the guest user identifier
   */
  static getGuestUserId(): string {
    return GUEST_USER_ID;
  }
}
